import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  HiCloudArrowUp,
  HiPencil,
  HiArrowDownTray,
  HiTrash,
  HiFolderPlus,
  HiArrowLeft
} from 'react-icons/hi2';
import { CgSpinner } from 'react-icons/cg';
import { RootState } from '../../redux/reducers';
import PageHeader from '../components/misc/PageHeader';
import PrimaryButton from '../components/buttons/PrimaryButton';
import {
  setCurrentDirectory,
  setShowCreateFolderModal,
  setShowDeleteConfirmModal,
  clearSelection,
  setLoading,
  setStorageUsage,
  setDirectoryTree,
  setItems,
  setUploading,
  setUploadProgress,
  addItems,
  setStorageLimitError,
  setError,
} from '../../redux/slices/storageSlice';
import storageService from '../../services/storageService';
import BreadcrumbNav from '../components/files/BreadcrumbNav';
import FileListView from '../components/files/FileListView';
import CreateFolderModal from '../components/files/CreateFolderModal';
import StorageLimitModal from '../components/files/StorageLimitModal';
import DeleteConfirmModal from '../components/files/DeleteConfirmModal';
import { StorageDirectory, StorageItem, StorageLimitError } from '../../types/storage';
import Button from '../components/buttons/Button';
import StorageUsageBar from '../components/files/StorageUsageBar';

export default function FilesPage(): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    item: StorageItem | null;
    directory: StorageDirectory | null;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const {
    currentDirectoryId,
    breadcrumbs,
    storageUsage,
    allDirectories,
    items,
    selectedItemIds,
    selectedDirectoryIds,
    sortBy,
    sortOrder,
    searchQuery,
    isLoading,
    isUploading,
    uploadProgress,
    showCreateFolderModal,
    showDeleteConfirmModal,
    showStorageLimitModal,
    storageLimitError,
  } = useSelector((state: RootState) => state.storage);

  const loadData = useCallback(async () => {
    try {
      const usage = await storageService.getStorageUsage();
      dispatch(setStorageUsage(usage));

      dispatch(setLoading(true));
      const tree = await storageService.getDirectoryTree();
      dispatch(setDirectoryTree(tree));

      const currentItems = await storageService.getItems(currentDirectoryId);
      dispatch(setItems(currentItems));
    } catch (error) {
      dispatch(setError((error as Error).message || 'Failed to load data'));
    }
  }, [dispatch, currentDirectoryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Removed redundant loadItems and its effect. Items are loaded via loadData.
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;

    try {
      dispatch(setUploading(true));
      const result = await storageService.uploadFiles(
        Array.from(files),
        currentDirectoryId ?? undefined,
        (progress) => {
          dispatch(setUploadProgress(progress));
        }
      );

      if ('usedStorage' in result) {
        dispatch(setStorageLimitError(result as StorageLimitError));
      } else {
        dispatch(addItems(result as StorageItem[]));

        const usage = await storageService.getStorageUsage();
        dispatch(setStorageUsage(usage));
      }
    } catch (error) {
      dispatch(setError((error as Error).message || 'Failed to upload files'));
    } finally {
      dispatch(setUploading(false));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    try {
      dispatch(setUploading(true));
      const result = await storageService.uploadFiles(
        files,
        currentDirectoryId ?? undefined,
        (progress) => {
          dispatch(setUploadProgress(progress));
        }
      );

      if ('usedStorage' in result) {
        dispatch(setStorageLimitError(result as StorageLimitError));
      } else {
        dispatch(addItems(result as StorageItem[]));
        const usage = await storageService.getStorageUsage();
        dispatch(setStorageUsage(usage));
      }
    } catch (error) {
      dispatch(setError((error as Error).message || 'Failed to upload files'));
    } finally {
      dispatch(setUploading(false));
    }
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    item: StorageItem | null,
    directory: StorageDirectory | null
  ) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item,
      directory,
    });
  };

  const handleDeleteSelected = () => {
    if (selectedItemIds.length > 0 || selectedDirectoryIds.length > 0) {
      dispatch(setShowDeleteConfirmModal(true));
    }
  };

  const handleNavigate = (directoryId: number | null) => {
    dispatch(setCurrentDirectory(directoryId));
  };

  const handleGoUp = () => {
    if (currentDirectoryId !== null) {
      const currentDir = allDirectories.find((d) => d.id === currentDirectoryId);
      if (currentDir) {
        dispatch(setCurrentDirectory(currentDir.parentId));
      }
    }
  };

  return (
    <div
      className="h-full flex flex-col"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <PageHeader
        title={t('page.files.title')}
        subtitle={t('page.files.subtitle')}
        className='pb-1'
        primaryButton={
          <div className='flex gap-2 justify-end'>
            <PrimaryButton
              text={isUploading ? `${t('page.files.uploading')} ${uploadProgress}%` : t('page.files.upload')}
              icon={HiCloudArrowUp}
              onClick={handleUploadClick}
              disabled={isUploading}
            />
            <Button
              variant="primary-outline"
              onClick={() => dispatch(setShowCreateFolderModal(true))}
              className="flex items-center gap-2 whitespace-nowrap">
              <HiFolderPlus className="h-4 w-4" />
              {t('page.files.newFolder')}
            </Button>
          </div>
        }
      />

      {/* Toolbar */}
      <div className="border-b border-secondary-200 px-6 py-4 flex items-center justify-between bg-secondary-50">
        <div className="flex items-center gap-3">
          <Button
            variant="tertiary"
            onClick={handleGoUp}
            disabled={currentDirectoryId === null}
            className="flex items-center gap-2"
            title={t('page.files.goUpOneLevel')}
          >
            <HiArrowLeft className="h-4 w-4" />
          </Button>
          <BreadcrumbNav breadcrumbs={breadcrumbs} onNavigate={handleNavigate} />
        </div>

        <div className="flex items-center gap-3">
          {/* Storage Usage Bar */}
          <StorageUsageBar usage={storageUsage} />

          {/* Bulk Actions */}
          {(selectedItemIds.length > 0 || selectedDirectoryIds.length > 0) && (
            <div className="flex items-center gap-2 pl-4 border-l border-secondary-200">
              <span className="text-sm text-primary-900 font-medium">
                {selectedItemIds.length + selectedDirectoryIds.length} {t('page.files.selected')}
              </span>
              <Button
                variant="danger-v2"
                onClick={handleDeleteSelected}
                className="text-xs"
              >
                {t('page.files.delete')}
              </Button>
              <Button
                variant="tertiary"
                onClick={() => dispatch(clearSelection())}
                className="text-xs"
              >
                {t('page.files.clear')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 relative">
        {dragOver && (
          <div className="absolute inset-6 border-4 border-dashed border-primary bg-primary-100/20 rounded-2xl flex items-center justify-center z-10">
            <div className="text-center">
              <HiCloudArrowUp className="w-16 h-16 mx-auto mb-4 text-primary" />
              <p className="text-lg font-semibold text-primary-900">{t('page.files.dropFilesHere')}</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <CgSpinner className="animate-spin h-12 w-12 text-primary" />
          </div>
        ) : (
          <FileListView
            directories={allDirectories}
            items={items}
            selectedItemIds={selectedItemIds}
            selectedDirectoryIds={selectedDirectoryIds}
            sortBy={sortBy}
            sortOrder={sortOrder}
            searchQuery={searchQuery}
            currentDirectoryId={currentDirectoryId}
            onUpload={handleUploadClick}
            onContextMenu={handleContextMenu}
          />
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white rounded-xl shadow-xl border border-secondary-200 py-1 z-50 min-w-[180px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-sm text-primary-900 hover:bg-secondary-200 flex items-center gap-2 transition-colors"
          >
            <HiPencil className="w-4 h-4" />
            {t('page.files.rename')}
          </button>
          {contextMenu.item && (
            <a
              href={contextMenu.item.fileUrl}
              download
              className="w-full px-4 py-2.5 text-left text-sm text-primary-900 hover:bg-secondary-200 flex items-center gap-2 transition-colors"
            >
              <HiArrowDownTray className="w-4 h-4" />
              {t('page.files.download')}
            </a>
          )}
          <div className="border-t border-secondary-200 my-1" />
          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-sm text-danger hover:bg-danger/10 flex items-center gap-2 transition-colors"
          >
            <HiTrash className="w-4 h-4" />
            {t('page.files.delete')}
          </button>
        </div>
      )}

      {/* Modals */}
      <CreateFolderModal isOpen={showCreateFolderModal} currentDirectoryId={currentDirectoryId} />
      <DeleteConfirmModal isOpen={showDeleteConfirmModal} />
      <StorageLimitModal isOpen={showStorageLimitModal} error={storageLimitError} />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
